import { useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Dropdown,
  Input,
  Space,
  Popover,
  Skeleton,
  Checkbox,
  Select,
  Tag,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  DownloadOutlined,
  SettingOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { fetchCurrentUser, updateUser } from "../services/authAPI";
import {
  deleteChangeOrder,
  fetchAllChangeOrders,
  fetchContracts,
} from "../services/contractsAPI";
import { fetchVendors } from "../services/vendorsAPI";
import { fetchProjectUsers } from "../services/projectsAPI";
import NewOrUpdateChangeOrder from "../components/Contracts/NewOrUpdateChangeOrder";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";
import { formatDate } from "../utils/dateUtils";
import {
  applyDateFiltersToQueryParams,
  createGetColumnDateRangeProps,
} from "../utils/dateFilterUtils";
import { getChangeOrderLabel, isTimeChangeOrdersEnabled, TIME_CHANGE_ORDER_COLUMNS, formatDurationDays, getTimeAwareColumnPreference, persistTimeAwareColumns } from "../utils/projectUtils";
import { exportChangeOrdersToPDF } from "../utils/pdfUtils";
import { downloadBlob, withFileExtension } from "../utils/downloadUtils";

const CHANGE_ORDER_DATE_FILTER_KEYS = ["invoiced_date", "paid_date"];

const DEFAULT_CHANGE_ORDER_COLUMNS = [
  "number",
  "title",
  "contract_title",
  "contract_company",
  "status",
  "assignee",
  "invoiced_date",
  "paid_date",
  "amount",
  "action",
];

const getInitialChangeOrderColumns = (savedColumns) =>
  getTimeAwareColumnPreference({
    savedColumns,
    defaultColumns: DEFAULT_CHANGE_ORDER_COLUMNS,
    timeColumns: TIME_CHANGE_ORDER_COLUMNS,
  });

const buildChangeOrderFiltersForAPI = (filters) =>
  applyDateFiltersToQueryParams(filters, CHANGE_ORDER_DATE_FILTER_KEYS);

const ChangeOrders = () => {
  const { selectedProject, currentUser, setCurrentUser } = useAuth();
  const projectId = selectedProject.project_id;
  const userRole = selectedProject.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const coLabel = getChangeOrderLabel(selectedProject);
  const coLabelPlural = getChangeOrderLabel(selectedProject, true);
  const timeChangeOrdersEnabled = isTimeChangeOrdersEnabled(selectedProject);
  const queryClient = useQueryClient();
  const [searchedColumns, setSearchedColumns] = useState("");
  const [filters, setFilters] = useState({});
  const searchInput = useRef(null);
  const [ownerPagination, setOwnerPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("change_orders"),
    total: 0,
  });
  const [vendorPagination, setVendorPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("change_orders"),
    total: 0,
  });
  const dt = useRef(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [
    isVisibleNewOrUpdateChangeOrder,
    issetIsVisibleNewOrUpdateChangeOrder,
  ] = useState(false);
  const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() =>
    getInitialChangeOrderColumns(currentUser?.change_order_columns),
  );

  const [isViewMode, setIsViewMode] = useState(false);
  const [tempSelection, setTempSelection] = useState(visibleColumns);
  const [ownerTableKey, setOwnerTableKey] = useState(0);
  const [vendorTableKey, setVendorTableKey] = useState(0);
  const [exportPdfLoading, setExportPdfLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const getColumnDateRangeProps = createGetColumnDateRangeProps(
    filters,
    setFilters,
  );

  const changeOrderQueryFilters = buildChangeOrderFiltersForAPI(filters);

  const { data: ownerChangeOrders, isLoading: isLoadingOwner } = useQuery({
    queryKey: [
      "changeOrders",
      projectId,
      "owner",
      currentUser,
      ownerPagination.current,
      ownerPagination.pageSize,
      changeOrderQueryFilters,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      fetchAllChangeOrders(
        projectId,
        ownerPagination.current,
        ownerPagination.pageSize,
        changeOrderQueryFilters,
        { sortField, sortOrder, party: "owner" },
      ),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: vendorChangeOrders, isLoading: isLoadingVendor } = useQuery({
    queryKey: [
      "changeOrders",
      projectId,
      "vendor",
      currentUser,
      vendorPagination.current,
      vendorPagination.pageSize,
      changeOrderQueryFilters,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      fetchAllChangeOrders(
        projectId,
        vendorPagination.current,
        vendorPagination.pageSize,
        changeOrderQueryFilters,
        { sortField, sortOrder, party: "vendor" },
      ),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  // fetch contracts
  const { data: contracts } = useQuery({
    queryKey: ["contracts", projectId, currentUser, null, null, {}],
    queryFn: () => fetchContracts(projectId, null, null, {}),
    enabled: !!projectId,
  });

  const handleDownload = async (record) => {
    try {
      const response = await fetch(record?.coversheet_url);
      if (!response.ok) {
        throw new Error("Download failed");
      }
      const blob = await response.blob();
      const pdfBlob =
        blob.type === "application/pdf"
          ? blob
          : new Blob([blob], { type: "application/pdf" });

      const filenamePrefix =
        coLabel === "Contract Instruction" ? "CI" : "CO";
      const filename = withFileExtension(
        `${filenamePrefix}_${record?.number}_${record?.title}`
          .replace(/[^\w\s.-]/g, "")
          .replace(/\s+/g, "_"),
        "pdf",
      );

      downloadBlob(pdfBlob, filename);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  useEffect(() => {
    setOwnerPagination((prev) => ({
      ...prev,
      current: 1,
      total: undefined,
    }));
    setVendorPagination((prev) => ({
      ...prev,
      current: 1,
      total: undefined,
    }));
  }, [filters]);

  useEffect(() => {
    const initialColumns = getInitialChangeOrderColumns(
      currentUser?.change_order_columns,
    );
    setVisibleColumns(initialColumns);
    setTempSelection(initialColumns);
  }, [currentUser?.change_order_columns, projectId]);

  const { data: projectVendors } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: projectUsers } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const handlePartyTableChange = (newPagination, _filters, sorter, setPartyPagination) => {
    const nextSortField = sorter?.order ? sorter.columnKey : null;
    const nextSortOrder =
      sorter?.order === "ascend" ? "asc" : sorter?.order === "descend" ? "desc" : null;
    const isSortChanged = nextSortField !== sortField || nextSortOrder !== sortOrder;
    setSortField(nextSortField);
    setSortOrder(nextSortOrder);
    if (isSortChanged) {
      setOwnerPagination((prev) => ({ ...prev, current: 1 }));
      setVendorPagination((prev) => ({ ...prev, current: 1 }));
    }
    setPartyPagination(isSortChanged ? { ...newPagination, current: 1 } : newPagination);
    savePreferredPageSize(newPagination.pageSize, "change_orders");
  };

  const getColumnSortOrder = (key) =>
    sortField === key ? (sortOrder === "desc" ? "descend" : "ascend") : null;

  const columnSetMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: async (data, variables) => {
      const user = await fetchCurrentUser();
      localStorage.setItem("user", JSON.stringify(user));
      setCurrentUser(user);
      setIsPopoverOpen(false);
      const nextColumns = getInitialChangeOrderColumns(
        variables.change_order_columns,
      );
      setVisibleColumns(nextColumns);
      setTempSelection(nextColumns);
      setIsLoadingColumns(false);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update columns",
        description: error.message,
      });
      setIsLoadingColumns(false);
    },
  });

  const handleSave = () => {
    setIsLoadingColumns(true);
    columnSetMutation.mutate({
      ...currentUser,
      change_order_columns: persistTimeAwareColumns({
        selection: tempSelection,
        previousPreference: visibleColumns,
        enabled: timeChangeOrdersEnabled,
        timeColumns: TIME_CHANGE_ORDER_COLUMNS,
      }),
    });
  };

  const statuses = [
    { label: "Draft", value: "draft" },
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  const handleCancel = () => {
    setTempSelection(visibleColumns);
    setIsPopoverOpen(false);
  };

  const deleteMutation = useMutation({
    mutationFn: ({ contractId, changeOrderId, projectId }) =>
      deleteChangeOrder({ contractId, changeOrderId, projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries(["change-orders", projectId]);
      queryClient.invalidateQueries(["changeOrders", projectId]);
      notification.success({ message: `${coLabel} deleted successfully` });
    },
    onError: (error) => {
      notification.error({
        message: `Failed to delete ${coLabel}`,
        description: error.message,
      });
    },
  });
  const handleUpdateClick = (changeOrder) => {
    setSelectedChangeOrder(changeOrder);
    setIsViewMode(false);
    issetIsVisibleNewOrUpdateChangeOrder(true);
  };

  const handleViewClick = (changeOrder) => {
    setSelectedChangeOrder(changeOrder);
    setIsViewMode(true);
    issetIsVisibleNewOrUpdateChangeOrder(true);
  };

  const handleDeleteClick = (contractId, changeOrderId) => {
    deleteMutation.mutate({
      contractId,
      changeOrderId,
      projectId,
    });
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    if (!searchedColumns.includes(dataIndex)) {
      setSearchedColumns([...searchedColumns, dataIndex]);
    }
    setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    setSearchedColumns((prev) => prev.filter((col) => col !== dataIndex));
    clearFilters();
    confirm();
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
  };

  const getColumnSearchProps = (
    dataIndex,
    type = "text",
    selectOptions = []
  ) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <>
          {type === "select" ? (
            <Select
              showSearch
              allowClear
              placeholder={`Select ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={(value) => {
                setSelectedKeys(value !== undefined ? [value] : []);
              }}
              style={{ marginBottom: 8, display: "block" }}
              options={selectOptions}
            />
          ) : (
            <Input
              ref={searchInput}
              placeholder={`Search ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                handleSearch(selectedKeys, confirm, dataIndex)
              }
              style={{ marginBottom: 8, display: "block" }}
            />
          )}
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() =>
                clearFilters && handleReset(clearFilters, confirm, dataIndex)
              }
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const target = record[dataIndex];

      if (!target) return false;

      if (typeof target === "object" && target?.name) {
        return target.name === value;
      }

      if (type === "select") {
        return target === value;
      }

      return target
        .toString()
        .toLowerCase()
        .includes(value.toString().toLowerCase());
    },
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const contractTitleOptions = [
    ...(contracts?.owner_contract
      ? [
          {
            value: contracts.owner_contract._id || contracts.owner_contract.id,
            label: contracts.owner_contract.title,
          },
        ]
      : []),
    ...(contracts?.contracts?.map((contract) => ({
      value: contract._id || contract.id,
      label: contract.title,
    })) || []),
  ];

  const columns = [
    {
      title: "Number",
      dataIndex: "number",
      key: "number",
      isVisible: visibleColumns.includes("number"),
      csvDataIndex: "number",
      sorter: true,
      sortOrder: getColumnSortOrder("number"),
      ...getColumnSearchProps("number"),
    },

    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      isVisible: visibleColumns.includes("title"),
      sorter: true,
      sortOrder: getColumnSortOrder("title"),
      ...getColumnSearchProps("title"),
      csvDataIndex: "title",
    },

    {
      title: "Contract Company",
      dataIndex: "contract",
      key: "contract_company",
      isVisible: visibleColumns.includes("contract_company"),
      sorter: true,
      sortOrder: getColumnSortOrder("contract_company"),
      filterDropdown: getColumnSearchProps(
        "contract_company",
        "select",
        projectVendors?.vendors?.map((v) => ({ value: v.id, label: v.name }))
      ).filterDropdown,
      filterIcon: getColumnSearchProps(
        "contract_company",
        "select",
        projectVendors?.vendors?.map((v) => ({ value: v.id, label: v.name }))
      ).filterIcon,
      render: (contract) => contract?.contract_company?.name || "-",
      csvDataIndex: "contract",
    },
    {
      title: "Contract Title",
      dataIndex: "contract",
      key: "contract_title",
      isVisible: visibleColumns.includes("contract_title"),
      sorter: true,
      sortOrder: getColumnSortOrder("contract_title"),
      filterDropdown: getColumnSearchProps(
        "contract_title",
        "select",
        contractTitleOptions
      ).filterDropdown,
      filterIcon: getColumnSearchProps(
        "contract_title",
        "select",
        contractTitleOptions
      ).filterIcon,
      render: (contract) => contract?.title || "-",
      csvDataIndex: "contract",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      isVisible: visibleColumns.includes("status"),
      csvDataIndex: "status",
      ...getColumnSearchProps("status", "select", statuses),
      render: (status) => {
        if (!status) return <span>-</span>;

        const statusItem = statuses.find((s) => s.value === status);
        const formattedStatus = statusItem ? statusItem.label : status;
        const statusColorMap = {
          draft: "blue",
          pending: "orange",
          approved: "green",
          rejected: "red",
        };

        return (
          <Tag color={statusColorMap[status] || "black"}>{formattedStatus}</Tag>
        );
      },
    },
    {
      title: "Assignee",
      dataIndex: "assignee_id",
      key: "assignee",
      isVisible: visibleColumns.includes("assignee"),
      csvDataIndex: "assignee_id",
      filterDropdown: getColumnSearchProps(
        "assignee",
        "select",
        projectUsers?.data?.map((user) => ({
          value: user.id,
          label: `${user.username} ${
            user.vendor_name ? `(${user.vendor_name})` : ""
          }`,
        }))
      ).filterDropdown,
      filterIcon: getColumnSearchProps(
        "assignee",
        "select",
        projectUsers?.data?.map((user) => ({
          value: user.id,
          label: `${user.username} ${
            user.vendor_name ? `(${user.vendor_name})` : ""
          }`,
        }))
      ).filterIcon,
      render: (assigneeId) => {
        if (!assigneeId) return <span>-</span>;
        const assignee = projectUsers?.data?.find(
          (user) => user.id === assigneeId
        );
        return assignee
          ? `${assignee.username} ${
              assignee.vendor_name ? `(${assignee.vendor_name})` : ""
            }`
          : "-";
      },
    },

    {
      title: "Invoiced Date",
      dataIndex: "invoiced_date",
      key: "invoiced_date",
      ...getColumnDateRangeProps("invoiced_date", "Invoiced Date"),
      render: (text) => formatDate(text),
      isVisible: visibleColumns.includes("invoiced_date"),
    },
    {
      title: "Paid Date",
      dataIndex: "paid_date",
      key: "paid_date",
      ...getColumnDateRangeProps("paid_date", "Paid Date"),
      render: (text) => formatDate(text),
      isVisible: visibleColumns.includes("paid_date"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatCurrency(amount),
      isVisible: visibleColumns.includes("amount"),
    },
    ...(timeChangeOrdersEnabled
      ? [
          {
            title: "Days",
            dataIndex: "days",
            key: "days",
            render: (days) => formatDurationDays(days),
            isVisible: visibleColumns.includes("days"),
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const actionItems = [
          {
            key: "1",
            label: <Button className="w-full" onClick={() => handleViewClick(record)}>View</Button>,
          },
          ...(isOwnerOrManager
            ? [
                {
                  key: "2",
                  label: <Button className="w-full" onClick={() => handleUpdateClick(record)}>Update</Button>,
                },
                {
                  key: "3",
                  label: (
                    <Popconfirm
                      title={`Are you sure you want to delete this ${coLabel.toLowerCase()}?`}
                      onConfirm={() =>
                        handleDeleteClick(record?.contract_id, record?._id)
                      }
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button className="w-full" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  ),
                },
              ]
            : []),
        ];
        return (
          <Dropdown menu={{ items: actionItems }} trigger={["click"]}>
            <Space>
              <Button icon={<EditOutlined />} size="small" />
              {record.coversheet_url && (
                <Button
                  icon={<DownloadOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(record);
                  }}
                  title="Download PDF"
                />
              )}
            </Space>
          </Dropdown>
        );
      },
      isVisible: visibleColumns.includes("action"),
    },
  ];

  const content = (
    <div style={{ width: 200 }}>
      <Checkbox.Group
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
        value={tempSelection}
        onChange={setTempSelection}
      >
        {columns.map((col) => (
          <Checkbox key={col.key} value={col.key}>
            {col.title}
          </Checkbox>
        ))}
      </Checkbox.Group>
      <div className="flex justify-end mt-3 gap-2">
        <Button size="small" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="primary" size="small" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );

  const handleClearAllFilters = () => {
    setFilters({});
    setSearchedColumns([]);
    setSortField(null);
    setSortOrder(null);
    setOwnerTableKey((prev) => prev + 1);
    setVendorTableKey((prev) => prev + 1);
  };

  const handleExportPDF = async () => {
    setExportPdfLoading(true);
    try {
      const filterPayload = buildChangeOrderFiltersForAPI(filters);
      const fetchPartyForExport = async (party, total) => {
        if (!total) return [];
        const data = await fetchAllChangeOrders(
          projectId,
          1,
          Math.min(total, 10000),
          filterPayload,
          { isExportPdf: true, sortField, sortOrder, party },
        );
        return data?.change_orders || [];
      };
      const [ownerRows, vendorRows] = await Promise.all([
        fetchPartyForExport("owner", ownerChangeOrders?.total || 0),
        fetchPartyForExport("vendor", vendorChangeOrders?.total || 0),
      ]);
      if (!ownerRows.length && !vendorRows.length) {
        notification.warning({
          message: "Nothing to export",
          description: `No ${coLabelPlural.toLowerCase()} match the current filters.`,
        });
        return;
      }
      exportChangeOrdersToPDF(
        [...ownerRows, ...vendorRows],
        selectedProject?.project_name || "",
        {
          reportLabel: coLabelPlural,
          includeDays: timeChangeOrdersEnabled,
          sections: [
            { title: `${coLabelPlural} with Owner`, rows: ownerRows },
            { title: `${coLabelPlural} with Vendors`, rows: vendorRows },
          ],
        },
      );
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error?.message || String(error),
      });
    } finally {
      setExportPdfLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">{coLabelPlural}</h1>
      <div className="flex gap-4 mb-4">
        {isOwnerOrManager && (
          <Button
            type="primary"
            onClick={() => {
              setIsViewMode(false);
              setSelectedChangeOrder(null);
              issetIsVisibleNewOrUpdateChangeOrder(true);
            }}
          >
            Create
          </Button>
        )}
        {isOwnerOrManager && (
          <Button
            icon={<DownloadOutlined />}
            size="middle"
            loading={exportPdfLoading}
            onClick={handleExportPDF}
          >
            Export PDF
          </Button>
        )}
        <Popover
          content={isLoadingColumns ? <Skeleton active /> : content}
          title="Select Columns to Display"
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(visible) => setIsPopoverOpen(visible)}
        >
          <Button icon={<SettingOutlined />}>Columns</Button>
        </Popover>
        <Button
          icon={<ClearOutlined />}
          onClick={handleClearAllFilters}
          className="ml-auto"
        >
          Clear All Filters
        </Button>
      </div>
      <div className="mb-8 overflow-hidden rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
          {coLabelPlural} with Owner
        </div>
        <Table
          columns={columns.filter((col) => col.isVisible === true)}
          dataSource={ownerChangeOrders?.change_orders || []}
          rowKey={(record) => record.id || record._id}
          key={ownerTableKey}
          loading={isLoadingOwner}
          pagination={{
            current: ownerPagination.current,
            pageSize: ownerPagination.pageSize,
            total: ownerChangeOrders?.total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={(pagination, filters, sorter) =>
            handlePartyTableChange(
              pagination,
              filters,
              sorter,
              setOwnerPagination,
            )
          }
        />
      </div>
      <div className="overflow-hidden rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
          {coLabelPlural} with Vendors
        </div>
        <Table
          ref={dt}
          columns={columns.filter((col) => col.isVisible === true)}
          dataSource={vendorChangeOrders?.change_orders || []}
          rowKey={(record) => record.id || record._id}
          key={vendorTableKey}
          loading={isLoadingVendor}
          pagination={{
            current: vendorPagination.current,
            pageSize: vendorPagination.pageSize,
            total: vendorChangeOrders?.total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={(pagination, filters, sorter) =>
            handlePartyTableChange(
              pagination,
              filters,
              sorter,
              setVendorPagination,
            )
          }
        />
      </div>
      {isVisibleNewOrUpdateChangeOrder && (
        <NewOrUpdateChangeOrder
          visible={isVisibleNewOrUpdateChangeOrder}
          onClose={() => {
            issetIsVisibleNewOrUpdateChangeOrder(false);
          }}
          isViewMode={isViewMode}
          changeOrderData={selectedChangeOrder}
        />
      )}
    </div>
  );
};

export default ChangeOrders;
